import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { notificationApi } from '@/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SegmentedTabs,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';
import { relativeTime } from '@/utils/format';

const CATEGORY_TONES = {
  APPLICATIONS: 'brand',
  INTERVIEWS: 'success',
  OFFERS: 'warning',
  MESSAGES: 'brand',
  RECOMMENDATIONS: 'success',
  ACCOUNT: 'neutral',
  ADMIN: 'warning',
};

/** The tile beside each row, so a category reads before its label does. */
const CATEGORY_ICONS = {
  APPLICATIONS: 'application',
  INTERVIEWS: 'calendar',
  OFFERS: 'star',
  MESSAGES: 'chat',
  RECOMMENDATIONS: 'sparkles',
  ACCOUNT: 'settings',
  ADMIN: 'shieldCheck',
};

/**
 * Admin alerts all share one category, so the tile says which queue each one
 * came from instead.
 */
const TYPE_ICONS = {
  ADMIN_STUDENT_REGISTERED: 'student',
  ADMIN_COMPANY_REGISTERED: 'company',
  ADMIN_INTERNSHIP_SUBMITTED: 'internship',
  ADMIN_COMPANY_VERIFICATION_REQUESTED: 'verification',
  ADMIN_CONTENT_REPORTED: 'flag',
  ADMIN_DOCUMENT_SUBMITTED: 'fileText',
};

/** The badge for an admin alert names its queue, not just "Admin". */
const TYPE_LABELS = {
  ADMIN_STUDENT_REGISTERED: 'New student',
  ADMIN_COMPANY_REGISTERED: 'New company',
  ADMIN_INTERNSHIP_SUBMITTED: 'Internship review',
  ADMIN_COMPANY_VERIFICATION_REQUESTED: 'Verification',
  ADMIN_CONTENT_REPORTED: 'Report',
  ADMIN_DOCUMENT_SUBMITTED: 'Document',
};

const ICON_TILES = {
  brand: 'bg-brand-50 text-brand-600 ring-brand-100',
  success: 'bg-success-50 text-success-700 ring-success-100',
  warning: 'bg-warning-50 text-warning-800 ring-warning-100',
  neutral: 'bg-surface-muted text-ink-muted ring-line',
};

const CATEGORY_LABELS = {
  APPLICATIONS: 'Applications',
  INTERVIEWS: 'Interviews',
  OFFERS: 'Offers',
  MESSAGES: 'Messages',
  RECOMMENDATIONS: 'Recommendations',
  ACCOUNT: 'Account',
  ADMIN: 'Admin',
};

const SWITCHES = [
  {
    field: 'email_applications',
    label: 'Applications',
    hint: 'New applications, status changes and withdrawals.',
    roles: ['STUDENT', 'COMPANY'],
  },
  {
    field: 'email_interviews',
    label: 'Interviews',
    hint: 'Invitations, changes of time and answers.',
    roles: ['STUDENT', 'COMPANY'],
  },
  {
    field: 'email_offers',
    label: 'Offers',
    hint: 'Offers made, withdrawn and answered.',
    roles: ['STUDENT', 'COMPANY'],
  },
  {
    field: 'email_messages',
    label: 'Messages',
    hint: 'New messages in your conversations.',
    roles: ['STUDENT', 'COMPANY'],
  },
  {
    field: 'email_recommendations',
    label: 'Recommendations',
    hint: 'New internships that match your profile.',
    roles: ['STUDENT'],
  },
  {
    field: 'email_account',
    label: 'Account',
    hint: 'Verification decisions, platform announcements and notices.',
  },
];

const TABS = [
  { key: false, label: 'All' },
  { key: true, label: 'Unread' },
];

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const params = { page, page_size: 20, ...(unreadOnly ? { unread_only: true } : {}) };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationApi.listNotifications(params),
  });

  const { data: preferences } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: notificationApi.getPreferences,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['notification-count'] });
  }

  const read = useMutation({
    mutationFn: (id) => notificationApi.markRead(id),
    onSuccess: invalidate,
  });

  const readAll = useMutation({
    mutationFn: notificationApi.markAllRead,
    onSuccess: (result) => {
      toast.success(
        result.marked === 0 ? 'Nothing to catch up on' : `${result.marked} marked as read`,
      );
      invalidate();
    },
    onError: (readError) => toast.error(errorMessage(readError, 'Could not mark them read')),
  });

  const remove = useMutation({
    mutationFn: (id) => notificationApi.deleteNotification(id),
    onSuccess: invalidate,
    onError: (removeError) => toast.error(errorMessage(removeError, 'Could not remove it')),
  });

  const savePreferences = useMutation({
    mutationFn: (input) => notificationApi.updatePreferences(input),
    onSuccess: () => {
      toast.success('Email preferences saved');
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save')),
  });

  if (isPending) return <LoadingState label="Loading your notifications…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Notifications"
        description="Everything the platform has told you."
        action={
          <Button
            variant="secondary"
            size="sm"
            isLoading={readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            <Icon name="check" size={15} />
            Mark all read
          </Button>
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <ListCard
          toolbarStart={
            <SegmentedTabs
              variant="plain"
              label="Show"
              tabs={TABS}
              value={unreadOnly}
              onChange={(key) => {
                setUnreadOnly(key);
                setPage(1);
              }}
            />
          }
          footer={
            meta &&
            meta.total_pages > 1 && (
              <Pagination
                className=""
                page={meta.page}
                totalPages={meta.total_pages}
                totalItems={meta.total_items}
                onPageChange={setPage}
              />
            )
          }
        >
          {rows.length === 0 ? (
            <div className="p-5">
              <EmptyState
                icon={<Icon name="bell" size={22} />}
                title={unreadOnly ? 'All caught up' : 'Nothing yet'}
                description={
                  unreadOnly
                    ? 'You have read everything.'
                    : 'We will tell you here when something happens.'
                }
              />
            </div>
          ) : (
            <ul className="divide-line @container divide-y">
              {rows.map((notification) => {
                const unread = notification.read_at === null;
                const tone = CATEGORY_TONES[notification.category] ?? 'neutral';
                return (
                  <li
                    key={notification.id}
                    className={cn(
                      'flex flex-col gap-4 px-4 py-4 transition @xl:flex-row @xl:items-start sm:px-5',
                      unread ? 'bg-brand-50/40' : 'hover:bg-surface-muted/50',
                    )}
                  >
                    <div className="flex min-w-0 flex-1 gap-4">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'relative grid size-10 shrink-0 place-items-center rounded-xl ring-1 ring-inset',
                          ICON_TILES[tone] ?? ICON_TILES.neutral,
                        )}
                      >
                        <Icon
                          name={
                            TYPE_ICONS[notification.type] ??
                            CATEGORY_ICONS[notification.category] ??
                            'bell'
                          }
                          size={18}
                        />
                        {unread && (
                          <span className="bg-brand-600 ring-surface absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'text-ink flex flex-wrap items-center gap-2 text-[15px] leading-snug',
                            unread ? 'font-semibold' : 'font-medium',
                          )}
                        >
                          {notification.title}
                          <Badge tone={tone}>
                            {TYPE_LABELS[notification.type] ??
                              CATEGORY_LABELS[notification.category] ??
                              notification.category}
                          </Badge>
                          {unread && (
                            <Badge tone="brand" dot>
                              New
                            </Badge>
                          )}
                        </p>
                        <p className="text-ink-muted mt-1 text-sm whitespace-pre-line wrap-anywhere">
                          {notification.body}
                        </p>
                        <p className="text-ink-muted mt-2 inline-flex items-center gap-1.5 text-xs">
                          <Icon name="clock" size={14} />
                          {relativeTime(notification.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2 @xl:justify-end">
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        title={`Remove: ${notification.title}`}
                        onClick={() => remove.mutate(notification.id)}
                      >
                        <Icon name="trash" size={14} />
                        Remove
                      </Button>
                      {unread && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => read.mutate(notification.id)}
                        >
                          <Icon name="check" size={14} />
                          Mark read
                        </Button>
                      )}
                      {notification.link && (
                        // Opening a notification also marks it read.
                        <LinkButton
                          to={notification.link}
                          variant="tintBrand"
                          size="sm"
                          title={`Open: ${notification.title}`}
                          onClick={() => unread && read.mutate(notification.id)}
                        >
                          <Icon name="arrowRight" size={14} />
                          Open
                        </LinkButton>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ListCard>

        <Card>
          <CardHeader
            title="Email me about"
            description="Turning one off only stops the email — it still appears here."
          />
          {user?.role === 'SUPER_ADMIN' && (
            <p className="text-ink-muted mb-3 text-xs">
              Admin alerts — new accounts, listings to review, verification requests, reports and
              documents — appear here only and are never emailed.
            </p>
          )}
          <div className="divide-line divide-y">
            {SWITCHES.filter((entry) => !entry.roles || entry.roles.includes(user?.role)).map(
              (entry) => (
                <div key={entry.field} className="py-3 first:pt-0 last:pb-0">
                  <Checkbox
                    label={entry.label}
                    checked={preferences?.[entry.field] ?? true}
                    disabled={!preferences || savePreferences.isPending}
                    onChange={(event) =>
                      savePreferences.mutate({ [entry.field]: event.target.checked })
                    }
                  />
                  {/* Checkbox has no hint slot, so it sits alongside. */}
                  <p className="text-ink-muted mt-0.5 ml-6 text-xs">{entry.hint}</p>
                </div>
              ),
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
